/**
 * @description LYN-422 / LYN-426 / LYN-438 / LYN-446 Overview utilization, Account Mix,
 *   Profit vs pace, and cross-report lines. Derived from stored windows +
 *   Analysis jobs.
 *
 * @dependencies ./scope1Dashboard, ./scope1Pace
 *
 * @notes All is org and speaks in companies. Company speaks in trucks and
 *   drivers. A driver or truck appears on All only as a leaderboard or a
 *   needs-immediate-action callout. Account Mix is Analysis only: do not
 *   feed Utilization into the series. Truck and driver windows are not
 *   summed together (that would double invoice). Utilization surfaces sum
 *   the non-overlapping windows in the Time range. Overlap counts. A window
 *   that covers current and prior belongs to one. No jobs write.
 *   Never say "window" in operator copy. Needs Attention is one queue:
 *   immediate lines first, then Analysis, utilization, and cross-report.
 *   Pulse, utilization attention, and cross-report name the same Time
 *   grain Assets & Crew uses. Volume and Account Mix reuse timeGrainWords.
 *   A window total compares only to a same-length usual. Hour, day, and
 *   week usual is the prior window clipped to the elapsed clock.
 *   Month, quarter, and year omit the invoice percent. Account Mix is
 *   calls only.
 *   Account Mix grain follows the Account Type filter. All and Regular
 *   are types. An exact type token is accounts. No click drill.
 *   Residual readers: none.
   Cross-balance (flat unpaid share) is gated until an age-aware alarm
   exists (LYN-446).
 */

import {
  ACCOUNT_TYPE_ALL,
  ACCOUNT_TYPE_REGULAR,
  accountTypeLabel,
  formatSeconds,
  formatVolume,
  jobDurationSeconds,
  parseMinCallCount
} from './scope1Dashboard';
import {
  distinctPeriodWindows,
  highestYield,
  selectCoveredWindows,
  sameLengthPrior,
  skipUnlikeInvoice,
  storedPeriodVsPhrase,
  timeGrainWords,
  windowCountsForRange,
  windowFitsLockedRange
} from './assetsCrew';
import { jobsForResponse } from './scope1Pace';

const MS_YEAR = 365 * 24 * 60 * 60 * 1000;
const LOW_INVOICE_RATIO = 0.75;
const HIGH_CALLS_RATIO = 1.15;
const SHARE_LEADER = 0.4;

/**
 * @param {unknown} value
 * @returns {number}
 */
export function asNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Company or org name. Never a hardcoded brand.
 *
 * @param {{ view?: string, companyName?: string|null, salesOrgName?: string|null }} input
 * @returns {string}
 */
export function namedGrain({ view, companyName, salesOrgName } = {}) {
  if (view === 'all') {
    const org = typeof salesOrgName === 'string' ? salesOrgName.trim() : '';
    return org || 'the org';
  }
  const company = typeof companyName === 'string' ? companyName.trim() : '';
  return company || 'this company';
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function displayName(value) {
  return String(value || '').trim();
}

/**
 * Finest non-overlapping series per key. Overlaps keep the shorter window.
 *
 * @param {Array<object>} windows
 * @param {(row: object) => string} keyFn
 * @param {string} [filter]
 * @returns {Array<object>}
 */
export function latestWindowsByKey(windows, keyFn, filter, opts = {}) {
  const source = opts.preselected
    ? (windows || []).filter(Boolean)
    : ((opts.range && (
      filter === 'month' || filter === 'quarter' || filter === 'year' || filter === 'week'
    ))
      ? selectCoveredWindows(windows, opts.range, {
        filter,
        timeZone: opts.timeZone,
        now: opts.now,
        sibling: opts.sibling
      })
      : (windows || []).filter((row) => row && windowFitsLockedRange(row, filter)));
  const groups = new Map();
  for (const row of source) {
    const key = keyFn(row);
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }
  const out = [];
  for (const rows of groups.values()) {
    out.push(...distinctPeriodWindows(rows));
  }
  return out;
}

/**
 * @param {Array<object>} windows
 * @param {{ from?: Date|string, to?: Date|string }|null} range
 * @returns {Array<object>}
 */
export function windowsInRange(windows, range, sibling, opts = {}) {
  if (!range?.from || !range?.to) return windows || [];
  const from = Date.parse(range.from);
  const to = Date.parse(range.to);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return windows || [];
  if (opts.filter === 'month' || opts.filter === 'quarter' || opts.filter === 'year' || opts.filter === 'week') {
    return selectCoveredWindows(windows, range, {
      filter: opts.filter,
      timeZone: opts.timeZone,
      now: opts.now,
      sibling
    });
  }
  return (windows || []).filter((row) => windowCountsForRange(row, range, sibling));
}

/**
 * Truck grain when that company has trucks. Driver grain only for a
 * company with no trucks. Do not drop a driver-only company because
 * another company on the org has trucks.
 *
 * @param {Array<object>} windows
 * @param {string} [filter]
 * @returns {Array<object>}
 */
export function companyInvoiceWindows(windows, filter, opts = {}) {
  const source = (opts.range && (
    filter === 'month' || filter === 'quarter' || filter === 'year' || filter === 'week'
  ))
    ? selectCoveredWindows(windows, opts.range, {
      filter,
      timeZone: opts.timeZone,
      now: opts.now,
      sibling: opts.sibling
    })
    : (windows || []).filter((row) => row && windowFitsLockedRange(row, filter));
  const byCompany = new Map();
  for (const row of source) {
    const id = row.companyId || row.companyName || row.name || '';
    if (!byCompany.has(id)) byCompany.set(id, []);
    byCompany.get(id).push(row);
  }
  const out = [];
  for (const rows of byCompany.values()) {
    const trucks = latestWindowsByKey(
      rows.filter((row) => row.reportBy === 'truck'),
      (row) => `${row.companyId || ''}:${row.name || ''}`,
      filter,
      { preselected: true }
    );
    if (trucks.length > 0) {
      out.push(...trucks);
      continue;
    }
    out.push(...latestWindowsByKey(
      rows.filter((row) => row.reportBy === 'driver'),
      (row) => `${row.companyId || ''}:${row.name || ''}`,
      filter,
      { preselected: true }
    ));
  }
  return out;
}

/**
 * @param {Array<object>} windows
 * @returns {{ callCount: number, totalInvoiceCents: number, totalBalanceCents: number, averageInvoicePerCallCents: number }}
 */
export function sumMeasures(windows) {
  let callCount = 0;
  let totalInvoiceCents = 0;
  let totalBalanceCents = 0;
  for (const row of windows || []) {
    callCount += asNumber(row.callCount);
    totalInvoiceCents += asNumber(row.totalInvoiceCents);
    totalBalanceCents += asNumber(row.totalBalanceCents);
  }
  return {
    callCount,
    totalInvoiceCents,
    totalBalanceCents,
    averageInvoicePerCallCents: callCount > 0
      ? Math.round(totalInvoiceCents / callCount)
      : 0
  };
}

/**
 * @param {{ totalInvoiceCents?: number, totalBalanceCents?: number }} measures
 * @returns {number|null}
 */
export function unpaidShare(measures) {
  const invoice = asNumber(measures?.totalInvoiceCents);
  if (invoice <= 0) return null;
  return asNumber(measures?.totalBalanceCents) / invoice;
}

/**
 * @param {Array<number>} values
 * @returns {number|null}
 */
export function median(values) {
  const list = (values || []).filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (list.length === 0) return null;
  const mid = Math.floor(list.length / 2);
  if (list.length % 2 === 0) return (list[mid - 1] + list[mid]) / 2;
  return list[mid];
}

/**
 * Faster stays to the left (smaller seconds).
 *
 * @param {Array<object>} jobs
 * @param {'onScene'|'completed'} metric
 * @returns {number|null}
 */
export function medianResponseSeconds(jobs, metric) {
  return median(
    jobsForResponse(jobs).jobs
      .map((job) => jobDurationSeconds(job, metric))
      .filter((value) => value != null)
  );
}

/**
 * @param {Array<object>} jobs
 * @param {Date} [now]
 * @returns {boolean}
 */
export function hasFullYearHistory(jobs, now = new Date()) {
  const cutoff = now.getTime() - MS_YEAR;
  return (jobs || []).some((job) => {
    const stamp = Date.parse(job?.enrouteAt || job?.createdAt || job?.windowStart || '');
    return Number.isFinite(stamp) && stamp <= cutoff;
  });
}

/**
 * @param {number} current
 * @param {number} usual
 * @returns {number|null}
 */
export function percentDelta(current, usual) {
  const baseline = Number(usual);
  if (!Number.isFinite(baseline) || baseline === 0) return null;
  return (Number(current) - baseline) / baseline;
}

/**
 * @param {{ total?: number, usual?: number, filter?: string, grainName?: string }} input
 * @returns {string|null}
 */
export function volumeInsight({ total, usual, filter, grainName } = {}) {
  if (total == null || !Number.isFinite(Number(total))) return null;
  const phrase = timeGrainWords(filter).current;
  const who = grainName ? `${grainName}: ` : '';
  const delta = sameLengthPrior(filter) ? percentDelta(total, usual) : null;
  if (delta == null) {
    return `Insight: ${who}${formatVolume(total)} tows ${phrase}.`;
  }
  const pct = Math.round(Math.abs(delta) * 100);
  const vs = delta >= 0 ? 'above' : 'below';
  return `Insight: ${who}${formatVolume(total)} tows ${phrase}, ${pct}% ${vs} your average.`;
}

/**
 * @param {{ filter?: string, rows?: Array<object>, grainName?: string }} input
 * @returns {string|null}
 */
export function comparisonInsight({ filter, rows, grainName } = {}) {
  const list = rows || [];
  if (list.length === 0) return null;
  const who = grainName ? `${grainName}: ` : '';
  const current = list.find((row) => row.inProgress) || list[list.length - 1];
  const priors = list.filter((row) => row !== current);
  const currentTows = asNumber(current.tows ?? current.today);
  const usual = current.usual != null
    ? asNumber(current.usual)
    : (priors.length
      ? priors.reduce((sum, row) => sum + asNumber(row.tows ?? row.today), 0) / priors.length
      : null);
  const phraseByFilter = {
    hour: 'this hour',
    week: 'this week',
    month: 'this month',
    quarter: 'this quarter',
    year: 'this year'
  };
  const phrase = phraseByFilter[filter] || 'today';
  if (usual == null) {
    return `Insight: ${who}${phrase.replace(/^./, (ch) => ch.toUpperCase())} is ${formatVolume(currentTows)} tows.`;
  }
  const vs = currentTows >= usual ? 'above' : 'below';
  const priorNounByFilter = {
    hour: 'the 12 week average',
    day: 'the 12 week average',
    week: 'the last 12 weeks',
    month: 'the last 4 months',
    quarter: 'the last 2 quarters'
  };
  const priorNoun = priorNounByFilter[filter] || 'prior years';
  return `Insight: ${who}${phrase.replace(/^./, (ch) => ch.toUpperCase())} is ${formatVolume(currentTows)} tows, ${vs} ${priorNoun}.`;
}

/**
 * @param {Array<object>} windows
 * @returns {Array<{ companyId: string, name: string, measures: object }>}
 */
export function companyMeasureRows(windows, filter, opts = {}) {
  const groups = new Map();
  for (const row of companyInvoiceWindows(windows, filter, opts)) {
    const id = row.companyId || row.name;
    const name = displayName(row.companyName || row.name) || id;
    const current = groups.get(id) || { companyId: id, name, windows: [] };
    current.windows.push(row);
    groups.set(id, current);
  }
  return [...groups.values()].map((entry) => ({
    companyId: entry.companyId,
    name: entry.name,
    measures: sumMeasures(entry.windows)
  }));
}

/**
 * @param {Array<object>} windows
 * @param {'truck'|'driver'} reportBy
 * @returns {Array<object>}
 */
export function assetMeasureRows(windows, reportBy, filter, opts = {}) {
  const groups = new Map();
  for (const row of latestWindowsByKey(
    (windows || []).filter((row) => row.reportBy === reportBy),
    (row) => `${row.companyId || ''}:${row.name || ''}`,
    filter,
    opts
  )) {
    const key = `${row.companyId || ''}:${row.name || ''}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }
  return [...groups.values()].map((rows) => {
    const first = rows[0];
    return {
      id: first.id,
      companyId: first.companyId,
      name: displayName(first.name),
      reportBy,
      measures: sumMeasures(rows),
      window: first
    };
  });
}

/**
 * One rotating utilization pulse. Follows Time. Names the same grain
 * Assets & Crew uses. Hour last 24 hours. Day last 7 days. Week last
 * 8 weeks. Month, quarter, and year omit invoice percent lines.
 * Invoice lines also wait for full coverage on both windows.
 * Never say window.
 *
 * @param {object} input
 * @returns {{ id: string, text: string }|null}
 */
export function utilizationPulse({
  view,
  filter,
  grainName,
  currentMeasures,
  previousMeasures,
  companyRows = [],
  random = Math.random,
  invoiceComparable
} = {}) {
  const words = timeGrainWords(filter);
  const phrase = words.current;
  const who = grainName || (view === 'all' ? 'the org' : 'this company');
  const pool = [];
  const currentInvoice = asNumber(currentMeasures?.totalInvoiceCents);
  const previousInvoice = asNumber(previousMeasures?.totalInvoiceCents);
  const skipInvoice = skipUnlikeInvoice(filter) || invoiceComparable === false;
  const storedVs = storedPeriodVsPhrase(filter);
  const invoiceVs = storedVs || `vs ${words.prior}`;
  if (!skipInvoice && currentInvoice > 0 && previousInvoice > 0) {
    const delta = percentDelta(currentInvoice, previousInvoice);
    if (delta != null) {
      const pct = Math.round(Math.abs(delta) * 100);
      const vs = delta >= 0 ? 'up' : 'down';
      pool.push({
        id: 'util-invoice',
        text: `${who} invoice is ${vs} ${pct}% ${invoiceVs}.`
      });
    }
  }
  const currentAvg = asNumber(currentMeasures?.averageInvoicePerCallCents);
  const previousAvg = asNumber(previousMeasures?.averageInvoicePerCallCents);
  if (!skipInvoice && currentAvg > 0 && previousAvg > 0) {
    const delta = percentDelta(currentAvg, previousAvg);
    if (delta != null && Math.abs(delta) >= 0.05) {
      const pct = Math.round(Math.abs(delta) * 100);
      const vs = delta >= 0 ? 'up' : 'down';
      pool.push({
        id: 'util-avg',
        text: `${who} average invoice moved ${vs} ${pct}% ${invoiceVs}.`
      });
    }
  }
  if (view === 'all' && companyRows.length > 0) {
    const total = companyRows.reduce((sum, row) => sum + asNumber(row.measures.totalInvoiceCents), 0);
    const leader = [...companyRows].sort((a, b) => (
      asNumber(b.measures.totalInvoiceCents) - asNumber(a.measures.totalInvoiceCents)
    ))[0];
    if (total > 0 && leader) {
      const share = asNumber(leader.measures.totalInvoiceCents) / total;
      if (share >= SHARE_LEADER) {
        pool.push({
          id: `util-share:${leader.name}`,
          text: `${leader.name} is carrying ${Math.round(share * 100)}% of org invoice ${phrase}.`
        });
      }
    }
  }
  if (pool.length === 0) return null;
  const index = Math.min(pool.length - 1, Math.floor(Number(random()) * pool.length));
  return pool[index];
}

/**
 * Invoiced cell detail. Comparison when both windows are complete,
 * else per-call, else the partial coverage note. All view adds the
 * share leader when there is no comparison.
 *
 * @param {object} input
 * @returns {string|null}
 */
export function invoicedBarDetail({
  filter,
  currentMeasures,
  previousMeasures,
  invoiceComparable,
  coverage,
  view,
  companyRows = []
} = {}) {
  const words = timeGrainWords(filter);
  const currentInvoice = asNumber(currentMeasures?.totalInvoiceCents);
  const previousInvoice = asNumber(previousMeasures?.totalInvoiceCents);
  const currentAvg = asNumber(currentMeasures?.averageInvoicePerCallCents);
  const skipInvoice = skipUnlikeInvoice(filter) || invoiceComparable === false;
  if (!skipInvoice && currentInvoice > 0 && previousInvoice > 0) {
    const delta = percentDelta(currentInvoice, previousInvoice);
    if (delta != null) {
      const pct = Math.round(Math.abs(delta) * 100);
      const vs = delta >= 0 ? 'up' : 'down';
      return `Invoice is ${vs} ${pct}% vs ${words.prior}.`;
    }
  }
  const share = view === 'all' && companyRows.length > 0
    ? (() => {
      const total = companyRows.reduce((sum, row) => sum + asNumber(row.measures.totalInvoiceCents), 0);
      const leader = [...companyRows].sort((a, b) => (
        asNumber(b.measures.totalInvoiceCents) - asNumber(a.measures.totalInvoiceCents)
      ))[0];
      if (total <= 0 || !leader) return null;
      const portion = asNumber(leader.measures.totalInvoiceCents) / total;
      if (portion < SHARE_LEADER) return null;
      return `${leader.name} is carrying ${Math.round(portion * 100)}% of org invoice ${words.current}.`;
    })()
    : null;
  if (currentAvg > 0) {
    const perCall = `${formatInvoiceCents(currentAvg)} per call`;
    return share ? `${perCall}. ${share}` : perCall;
  }
  const present = Number(coverage?.present) || 0;
  const expected = Number(coverage?.expected) || 0;
  if (expected > 0 && present < expected) {
    const noun = filter === 'hour' ? 'hours' : filter === 'day' ? 'days' : filter === 'week' ? 'weeks' : 'periods';
    return `Partial, still collecting (${present} of ${expected} ${noun})`;
  }
  return share;
}

/**
 * @param {unknown} seconds
 * @returns {string|null}
 */
function formatMedianMinutes(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return null;
  return `${Math.round(Number(seconds) / 60)} min`;
}

/**
 * Measured invoice vs a peer or own-prior median. No cost or profit words.
 *
 * @param {object} input
 * @returns {string}
 */
function measuredInvoiceSentence({
  name,
  calls,
  invoiceCents,
  medianCalls,
  medianInvoice,
  phrase
}) {
  return `${name} ran ${formatVolume(calls)} calls at ${formatInvoiceCents(invoiceCents)} per call, vs a median of ${formatVolume(medianCalls)} calls / ${formatInvoiceCents(medianInvoice)} ${phrase}.`;
}

/**
 * Measured response and invoice vs the plotted medians.
 *
 * @param {object} input
 * @returns {string}
 */
function measuredPaceSentence({
  name,
  responseSeconds,
  invoiceCents,
  calls,
  medianResponse,
  medianInvoice,
  phrase
}) {
  return `${name} has a ${formatMedianMinutes(responseSeconds)} median response and ${formatInvoiceCents(invoiceCents)} per call over ${formatVolume(calls)} calls, vs a ${formatMedianMinutes(medianResponse)} / ${formatInvoiceCents(medianInvoice)} median ${phrase}.`;
}

/**
 * Actionable utilization lines. All names companies vs their own prior
 * same-length window. Company names units against a peer median.
 * Zero-call and immediate callouts are dropped.
 *
 * @param {object} input
 * @returns {Array<{ id: string, sentence: string, immediate?: boolean }>}
 */
export function utilizationAttention({
  view,
  companyRows = [],
  priorCompanyRows = [],
  truckRows = [],
  driverRows = [],
  filter,
  minCalls,
  invoiceComparable
} = {}) {
  const phrase = timeGrainWords(filter).current;
  const lines = [];
  const floor = parseMinCallCount(minCalls);
  if (view === 'all') {
    if (!skipUnlikeInvoice(filter) && invoiceComparable !== false) {
      const priorById = new Map(
        (priorCompanyRows || []).map((row) => [row.companyId, row])
      );
      for (const row of companyRows) {
        const prior = priorById.get(row.companyId);
        if (!prior) continue;
        const currentInvoice = asNumber(row.measures.totalInvoiceCents);
        const priorInvoice = asNumber(prior.measures.totalInvoiceCents);
        if (priorInvoice <= 0) continue;
        if (asNumber(row.measures.callCount) < floor) continue;
        if (currentInvoice < priorInvoice * LOW_INVOICE_RATIO) {
          lines.push({
            id: `off-pace:${row.name}`,
            sentence: `${row.name} is off pace on invoice ${phrase}.`
          });
        }
      }
    }
    return lines;
  }
  const peers = [...truckRows, ...driverRows].filter((row) => asNumber(row.measures.callCount) >= floor);
  const avgInvoice = median(peers.map((row) => asNumber(row.measures.averageInvoicePerCallCents)));
  const avgCalls = median(peers.map((row) => asNumber(row.measures.callCount)));
  if (avgInvoice != null && avgCalls != null) {
    for (const row of peers) {
      const calls = asNumber(row.measures.callCount);
      const invoice = asNumber(row.measures.averageInvoicePerCallCents);
      if (calls >= avgCalls * HIGH_CALLS_RATIO && invoice < avgInvoice * LOW_INVOICE_RATIO) {
        lines.push({
          id: `not-earning:${row.name}`,
          sentence: measuredInvoiceSentence({
            name: row.name,
            calls,
            invoiceCents: invoice,
            medianCalls: avgCalls,
            medianInvoice: avgInvoice,
            phrase
          })
        });
      }
    }
  }
  return lines;
}

/**
 * Two additive 4-grid tiles. Do not replace Analysis tiles.
 *
 * @param {object} input
 * @returns {Array<{ title: string, label: string, detail: string }>}
 */
export function utilizationRailTiles({ view, companyRows = [], truckRows = [], driverRows = [] } = {}) {
  if (view === 'all') {
    const ranked = [...companyRows].sort((a, b) => (
      asNumber(b.measures.totalInvoiceCents) - asNumber(a.measures.totalInvoiceCents)
    ));
    const top = ranked.slice(0, 3);
    const topLabel = top.length ? top.map((row) => row.name).join(', ') : '—';
    const yieldRows = highestYield(companyRows);
    return [
      {
        title: 'Top invoice',
        label: topLabel,
        detail: top.length
          ? `Top ${top.length} companies by Total Invoice.`
          : 'No invoice leaders.'
      },
      {
        title: 'Highest Yield',
        label: yieldRows.length ? yieldRows.map((row) => row.name).join(', ') : '—',
        detail: yieldRows.length
          ? `Top ${yieldRows.length} companies by invoice per call.`
          : 'Not enough calls yet to rank yield.'
      }
    ];
  }
  const assets = truckRows.length > 0 ? truckRows : driverRows;
  const ranked = [...assets].sort((a, b) => (
    asNumber(b.measures.totalInvoiceCents) - asNumber(a.measures.totalInvoiceCents)
  ));
  const top = ranked.slice(0, 3);
  const yieldRows = highestYield(assets);
  const noun = truckRows.length > 0 ? 'trucks' : 'drivers';
  return [
    {
      title: 'Top invoice',
      label: top.length ? top.map((row) => row.name).join(', ') : '—',
      detail: top.length
        ? `Top ${top.length} ${noun} by Total Invoice.`
        : 'No invoice leaders.'
    },
    {
      title: 'Highest Yield',
      label: yieldRows.length ? yieldRows.map((row) => row.name).join(', ') : '—',
      detail: yieldRows.length
        ? `Top ${yieldRows.length} ${noun} by invoice per call.`
        : 'Not enough calls yet to rank yield.'
    }
  ];
}

/**
 * @param {object} job
 * @param {'truck'|'driver'} reportBy
 * @returns {string}
 */
export function jobAssetName(job, reportBy) {
  return displayName(reportBy === 'truck' ? job?.unit : job?.driver);
}

/**
 * @param {Array<object>} jobs
 * @param {string} name
 * @param {'truck'|'driver'} reportBy
 * @returns {Array<object>}
 */
export function jobsForAsset(jobs, name, reportBy) {
  const needle = displayName(name).toLowerCase();
  if (!needle) return [];
  return (jobs || []).filter((job) => jobAssetName(job, reportBy).toLowerCase() === needle);
}

/**
 * Calls toward the Overview minimum. Stored callCount wins; otherwise the
 * matching job rows.
 *
 * @param {object} row
 * @param {Array<object>} matchingJobs
 * @returns {number}
 */
function namedCallCount(row, matchingJobs) {
  if (row?.measures && Object.prototype.hasOwnProperty.call(row.measures, 'callCount')) {
    return asNumber(row.measures.callCount);
  }
  return (matchingJobs || []).length;
}

/**
 * Company: one point per truck and per driver. All: one point per company.
 * Points under the Overview minimum are not plotted.
 *
 * @param {object} input
 * @returns {Array<object>}
 */
export function profitVsPacePoints({
  view,
  companyRows = [],
  truckRows = [],
  driverRows = [],
  jobs = [],
  metric,
  minCalls
} = {}) {
  const floor = parseMinCallCount(minCalls);
  if (view === 'all') {
    return companyRows.map((row) => {
      const companyJobs = (jobs || []).filter((job) => (
        job.companyId === row.companyId
        || displayName(job.companyName) === row.name
      ));
      return {
        id: row.companyId,
        companyId: row.companyId,
        name: row.name,
        grain: 'company',
        callCount: namedCallCount(row, companyJobs),
        x: medianResponseSeconds(companyJobs, metric),
        y: asNumber(row.measures.averageInvoicePerCallCents)
      };
    }).filter((point) => point.x != null && point.y > 0 && point.callCount >= floor);
  }
  return [...truckRows, ...driverRows].map((row) => {
    const assetJobs = jobsForAsset(jobs, row.name, row.reportBy);
    return {
      id: `${row.reportBy}:${row.companyId}:${row.name}`,
      companyId: row.companyId,
      name: row.name,
      grain: row.reportBy,
      callCount: namedCallCount(row, assetJobs),
      x: medianResponseSeconds(assetJobs, metric),
      y: asNumber(row.measures.averageInvoicePerCallCents)
    };
  }).filter((point) => point.x != null && point.y > 0 && point.callCount >= floor);
}

/**
 * @param {Array<object>} points
 * @returns {{ x: number|null, y: number|null }}
 */
export function profitVsPaceMedians(points) {
  return {
    x: median((points || []).map((point) => point.x)),
    y: median((points || []).map((point) => point.y))
  };
}

/**
 * @param {object} point
 * @param {{ x?: number|null, y?: number|null }} medians
 * @returns {'fast-high'|'fast-low'|'slow-high'|'slow-low'|null}
 */
export function profitQuadrant(point, medians) {
  if (point?.x == null || point?.y == null || medians?.x == null || medians?.y == null) {
    return null;
  }
  const fast = point.x <= medians.x;
  const high = point.y >= medians.y;
  if (fast && high) return 'fast-high';
  if (fast && !high) return 'fast-low';
  if (!fast && high) return 'slow-high';
  return 'slow-low';
}

export const QUADRANT_LABELS = Object.freeze({
  'fast-high': { label: 'Fast, high $', tone: 'green', color: '#17B26A' },
  'fast-low': { label: 'Fast, low $', tone: 'amber', color: '#F79009' },
  'slow-high': { label: 'Slow, high $', tone: 'amber', color: '#F79009' },
  'slow-low': { label: 'Slow, low $', tone: 'red', color: '#F04438' }
});

/** Key order matches the plot: earning on top, fast on the left. */
export const QUADRANT_GRID = Object.freeze([
  'fast-high',
  'slow-high',
  'fast-low',
  'slow-low'
]);

/**
 * Paint each point with its quadrant color. On a median line counts as fast or earning.
 * @param {Array<object>} points
 * @param {{ x?: number|null, y?: number|null }} medians
 * @returns {Array<object>}
 */
export function colorProfitPoints(points, medians) {
  return (points || []).map((point) => {
    const quadrant = profitQuadrant(point, medians);
    const spec = quadrant ? QUADRANT_LABELS[quadrant] : null;
    return {
      ...point,
      quadrant,
      color: spec ? spec.color : undefined
    };
  });
}

/**
 * @param {object} input
 * @returns {string|null}
 */
export function profitVsPaceInsight({ view, points = [], medians, filter } = {}) {
  if (points.length === 0 || medians?.x == null || medians?.y == null) return null;
  const grouped = { 'fast-high': [], 'fast-low': [], 'slow-high': [], 'slow-low': [] };
  for (const point of points) {
    const q = profitQuadrant(point, medians);
    if (q) grouped[q].push(point);
  }
  const phrase = timeGrainWords(filter).current;
  const named = grouped['slow-low'][0]
    || (view === 'all' ? grouped['fast-high'][0] : (
      grouped['fast-high'].find((point) => point.grain === 'truck') || grouped['fast-high'][0]
    ));
  if (!named || named.x == null || named.y == null) return null;
  return `Insight: ${measuredPaceSentence({
    name: named.name,
    responseSeconds: named.x,
    invoiceCents: named.y,
    calls: named.callCount || 0,
    medianResponse: medians.x,
    medianInvoice: medians.y,
    phrase
  })}`;
}

/**
 * All and Regular group by account type. An exact type token groups by account.
 *
 * @param {string} [accountType]
 * @returns {'accountType'|'accountName'}
 */
export function accountMixGrainField(accountType) {
  if (!accountType || accountType === ACCOUNT_TYPE_ALL || accountType === ACCOUNT_TYPE_REGULAR) {
    return 'accountType';
  }
  return 'accountName';
}

/**
 * One bottom-axis label. Names live on hover.
 *
 * @param {string} [accountType]
 * @returns {string}
 */
export function accountMixAxisLabel(accountType) {
  return accountMixGrainField(accountType) === 'accountName' ? 'Account' : 'Account type';
}

/**
 * Hover line. Name, calls, share.
 *
 * @param {object} input
 * @returns {string|null}
 */
export function accountMixHover({ row } = {}) {
  if (!row) return null;
  const name = displayName(row.label || row.key);
  if (!name) return null;
  const pct = Math.round(asNumber(row.share) * 100);
  return `${name} · ${asNumber(row.value)} calls · ${pct}%`;
}

/**
 * Analysis only. Rolling top 5. Calls. Grain is the field.
 *
 * @param {object} input
 * @returns {Array<{ key: string, label: string, value: number, share: number }>}
 */
export function accountMixRows({
  jobs = [],
  field = 'accountType'
} = {}) {
  const counts = new Map();
  for (const job of jobs || []) {
    const key = displayName(job?.[field]);
    if (!key) continue;
    const current = counts.get(key) || { key, calls: 0 };
    current.calls += 1;
    counts.set(key, current);
  }
  const rows = [...counts.values()].map((row) => ({
    key: row.key,
    label: field === 'accountType' ? (accountTypeLabel(row.key) || row.key) : row.key,
    calls: row.calls,
    value: row.calls
  })).filter((row) => row.value > 0);
  const total = rows.reduce((sum, row) => sum + row.value, 0);
  return rows
    .map((row) => ({ ...row, share: total > 0 ? row.value / total : 0 }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);
}

/**
 * @param {object} input
 * @returns {string|null}
 */
export function accountMixInsight({
  filter,
  currentRows = [],
  previousRows = [],
  selectedType,
  grainName
} = {}) {
  const top = currentRows[0];
  if (!top) return null;
  const words = timeGrainWords(filter);
  const phrase = words.current;
  const noun = 'calls';
  const prior = previousRows.find((row) => row.key === top.key);
  const usualPct = prior ? Math.round(prior.share * 100) : null;
  const pct = Math.round(top.share * 100);
  const likeForLike = sameLengthPrior(filter);
  if (selectedType) {
    const typeLabel = accountTypeLabel(selectedType) || selectedType;
    if (likeForLike && usualPct != null) {
      return `Insight: ${top.label} is ${pct}% of ${typeLabel} ${noun} ${phrase}. Usual is ${usualPct}%.`;
    }
    return `Insight: ${top.label} is ${pct}% of ${typeLabel} ${noun} ${phrase}.`;
  }
  const who = grainName ? `${grainName}: ` : '';
  if (likeForLike && usualPct != null && usualPct !== pct) {
    const vs = pct > usualPct ? 'up from' : 'down from';
    return `Insight: ${who}${top.label} is ${pct}% of ${noun} ${phrase}, ${vs} ${usualPct}% ${words.prior}.`;
  }
  return `Insight: ${who}${top.label} is ${pct}% of ${noun} ${phrase}.`;
}

/**
 * Only lines that need both Analysis response and Utilization money.
 *
 * @param {object} input
 * @returns {Array<{ id: string, sentence: string }>}
 */
export function crossReportLines({
  view,
  filter,
  companyRows = [],
  truckRows = [],
  driverRows = [],
  jobs = [],
  metric,
  minCalls
} = {}) {
  const phrase = timeGrainWords(filter).current;
  const floor = parseMinCallCount(minCalls);
  const lines = [];
  if (view === 'all') {
    const points = profitVsPacePoints({
      view: 'all',
      companyRows,
      jobs,
      metric,
      minCalls: floor
    });
    const medians = profitVsPaceMedians(points);
    for (const point of points) {
      if (profitQuadrant(point, medians) === 'slow-low') {
        lines.push({
          id: `cross-slow:${point.name}`,
          sentence: measuredPaceSentence({
            name: point.name,
            responseSeconds: point.x,
            invoiceCents: point.y,
            calls: point.callCount || 0,
            medianResponse: medians.x,
            medianInvoice: medians.y,
            phrase
          })
        });
      }
    }
    return lines;
  }
  const points = profitVsPacePoints({
    view: 'company',
    truckRows,
    driverRows,
    jobs,
    metric,
    minCalls: floor
  });
  const byGrain = {
    truck: points.filter((point) => point.grain === 'truck'),
    driver: points.filter((point) => point.grain === 'driver')
  };
  for (const grain of ['truck', 'driver']) {
    const grainPoints = byGrain[grain];
    const medians = profitVsPaceMedians(grainPoints);
    for (const point of grainPoints) {
      if (profitQuadrant(point, medians) === 'slow-low') {
        lines.push({
          id: `cross-slow:${point.name}`,
          sentence: measuredPaceSentence({
            name: point.name,
            responseSeconds: point.x,
            invoiceCents: point.y,
            calls: point.callCount || 0,
            medianResponse: medians.x,
            medianInvoice: medians.y,
            phrase
          })
        });
      }
    }
  }
  const peers = [...truckRows, ...driverRows].filter((row) => asNumber(row.measures.callCount) >= floor);
  const avgInvoice = median(peers.map((row) => asNumber(row.measures.averageInvoicePerCallCents)));
  const avgCalls = median(peers.map((row) => asNumber(row.measures.callCount)));
  if (avgInvoice != null && avgCalls != null) {
    for (const row of peers) {
      const calls = asNumber(row.measures.callCount);
      const invoice = asNumber(row.measures.averageInvoicePerCallCents);
      const hard = calls >= avgCalls * HIGH_CALLS_RATIO;
      const cheap = invoice < avgInvoice * LOW_INVOICE_RATIO;
      if (hard && cheap) continue;
      if (cheap && calls >= avgCalls && row.reportBy === 'driver') {
        lines.push({
          id: `cross-mix:${row.name}`,
          sentence: measuredInvoiceSentence({
            name: row.name,
            calls,
            invoiceCents: invoice,
            medianCalls: avgCalls,
            medianInvoice: avgInvoice,
            phrase
          })
        });
      }
    }
  }
  return lines;
}

/** How long each Needs Attention sentence stays up. */
export const NEEDS_ATTENTION_ROTATE_MS = 8000;

/**
 * Family order after immediate lines. Analysis, then utilization, then cross-report.
 * `immediate` and `zero` share a rank: the zero-calls callout leads with other zero lines.
 */
const NEEDS_ATTENTION_RANK = Object.freeze({
  late: 0,
  volume: 1,
  driverLow: 2,
  classMix: 3,
  unpaid: 4,
  'off-pace': 5,
  zero: 6,
  immediate: 6,
  'not-earning': 7,
  'cross-slow': 8,
  'cross-balance': 9,
  'cross-mix': 10
});

/**
 * @param {{ type?: string, id?: string }} line
 * @returns {string}
 */
function attentionFamily(line) {
  if (line?.type) return line.type;
  const id = String(line?.id || '');
  const split = id.indexOf(':');
  return split === -1 ? id : id.slice(0, split);
}

/**
 * @param {{ name?: string, id?: string }} line
 * @returns {string}
 */
function attentionName(line) {
  if (line?.name) return line.name;
  const id = String(line?.id || '');
  const split = id.indexOf(':');
  return split === -1 ? '' : id.slice(split + 1);
}

/**
 * One Needs Attention queue. Immediate lines lead. The rest follow Analysis,
 * then utilization, then cross-report. Lines already in Today's Highlights
 * are left out. One id appears once. A sentence already queued
 * does not rotate again under another family.
 *
 * @param {object} input
 * @returns {Array<{ id: string, sentence: string }>}
 */
export function needsAttentionQueue({
  analysis = [],
  utilization = [],
  cross = [],
  occupiedNames = [],
  occupiedFacts = []
} = {}) {
  const names = new Set((occupiedNames || []).filter(Boolean));
  const facts = new Set((occupiedFacts || []).filter(Boolean));
  const seen = new Set();
  const seenSentences = new Set();
  const rows = [];
  let order = 0;
  for (const line of [...(analysis || []), ...(utilization || []), ...(cross || [])]) {
    const sentence = line?.sentence || line?.text;
    if (!sentence) continue;
    const family = attentionFamily(line);
    const name = attentionName(line);
    if (line?.type && facts.has(line.type)) continue;
    if (name && names.has(name)) continue;
    const id = line.id || `${family}:${sentence}`;
    if (seen.has(id) || seenSentences.has(sentence)) continue;
    seen.add(id);
    seenSentences.add(sentence);
    rows.push({
      id,
      sentence,
      immediate: Boolean(line.immediate),
      rank: NEEDS_ATTENTION_RANK[family] ?? 20,
      order
    });
    order += 1;
  }
  rows.sort((a, b) => {
    if (a.immediate !== b.immediate) return a.immediate ? -1 : 1;
    if (a.rank !== b.rank) return a.rank - b.rank;
    return a.order - b.order;
  });
  return rows.map((row) => ({ id: row.id, sentence: row.sentence }));
}

/**
 * @param {Array<{ id?: string, sentence?: string, text?: string }>} lines
 * @param {() => number} [random]
 * @returns {{ id: string, sentence: string }|null}
 */
export function pickLine(lines, random = Math.random) {
  const list = (lines || []).filter((row) => row && (row.sentence || row.text));
  if (list.length === 0) return null;
  const immediate = list.filter((row) => row.immediate);
  const pool = immediate.length > 0 ? immediate : list;
  const index = Math.min(pool.length - 1, Math.floor(Number(random()) * pool.length));
  const chosen = pool[index];
  return {
    id: chosen.id,
    sentence: chosen.sentence || chosen.text
  };
}

/**
 * @param {number} cents
 * @returns {string}
 */
export function formatInvoiceCents(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(asNumber(cents) / 100);
}

export { formatSeconds, formatVolume };
